import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getDashboardOverview,
  getSalesTrend,
  getTopItems,
  getPeakHours,
  getCategoryPerformance,
  getOrderStatusBreakdown,
  getRestaurant,
  API_ORIGIN
} from '../../services/api';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../utils/formatPrice';
import PriceTag from '../../components/ui/PriceTag';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import AppShell from '../../components/layout/AppShell';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const { restaurantId } = useParams();
  const { t } = useTranslation();

  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orderStatus, setOrderStatus] = useState({ statusBreakdown: [], cancellationReasons: [] });
  const [currency, setCurrency] = useState(null);

  useEffect(() => {
    // Fetch restaurant currency alongside analytics
    getRestaurant(restaurantId).then(res => {
      if (res.data?.currency) setCurrency(res.data.currency);
    }).catch(() => {});
    fetchAllData();
  }, [restaurantId, period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        salesRes,
        itemsRes,
        hoursRes,
        categoriesRes,
        statusRes
      ] = await Promise.all([
        getDashboardOverview(restaurantId, period),
        getSalesTrend(restaurantId, period),
        getTopItems(restaurantId, period),
        getPeakHours(restaurantId, period),
        getCategoryPerformance(restaurantId, period),
        getOrderStatusBreakdown(restaurantId, period)
      ]);

      setOverview(overviewRes?.overview ?? null);
      setSalesTrend(salesRes?.salesData ?? []);
      setTopItems(itemsRes?.topItems ?? []);
      setPeakHours(hoursRes?.peakHours ?? []);
      setCategories((categoriesRes?.categories ?? []).map(c => ({
        ...c,
        category: c.category || t('analytics.uncategorized'),
        total_revenue: parseFloat(c.total_revenue) || 0
      })));
      setOrderStatus({
        statusBreakdown: statusRes?.statusBreakdown ?? [],
        cancellationReasons: statusRes?.cancellationReasons ?? []
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // String formatter — used by recharts tooltips/axes which only accept strings.
  // KPI cards render with <PriceTag> so OMR shows the official sign.
  const formatCurrency = (value) => formatPrice(value, currency);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_ORIGIN}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  // Warm, colorblind-aware chart palette
  // Mesoon chart palette — colorblind-aware: green · amber · blue · yellow · slate · violet
  const COLORS = ['#10B981', '#F59E0B', '#3B82F6', '#FBBF24', '#9CA3AF', '#7C3AED'];

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>{t('analytics.loading', 'Loading analytics...')}</p>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="analytics-dashboard">
        <p className="analytics-error">{t('common.error')}: Failed to load dashboard.</p>
      </div>
    );
  }

  const salesTrendFormatted = salesTrend.map((d) => ({
    ...d,
    date: typeof d.date === 'string' ? d.date.slice(0, 10) : d.date,
    revenue: parseFloat(d.revenue) || 0,
    order_count: Number(d.order_count) || 0
  }));

  return (
    <AppShell maxWidth={false}>
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <div className="header-left">
          <h1>{t('analytics.title', 'Analytics Dashboard')}</h1>
          <p className="header-subtitle">
            {t('analytics.subtitle', 'Track your restaurant performance')}
          </p>
        </div>

        <div className="period-selector">
          <button
            type="button"
            className={`period-btn ${period === 'day' ? 'active' : ''}`}
            onClick={() => setPeriod('day')}
          >
            {t('analytics.today', 'Today')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'week' ? 'active' : ''}`}
            onClick={() => setPeriod('week')}
          >
            {t('analytics.week', 'This Week')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'month' ? 'active' : ''}`}
            onClick={() => setPeriod('month')}
          >
            {t('analytics.month', 'This Month')}
          </button>
          <button
            type="button"
            className={`period-btn ${period === 'all' ? 'active' : ''}`}
            onClick={() => setPeriod('all')}
          >
            {t('analytics.allTime', 'All Time')}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card revenue">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">💰</span>
            <span className="kpi-label">{t('analytics.totalRevenue', 'Total Revenue')}</span>
          </div>
          <div className="kpi-value">
            <PriceTag amount={overview.totalRevenue} currency={currency} size="lg" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </div>
          <div className={`kpi-change ${overview.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
            {overview.revenueGrowth >= 0 ? '↑' : '↓'} {Math.abs(overview.revenueGrowth)}% {t('analytics.vsPrevious')}
          </div>
        </div>

        <div className="kpi-card orders">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">📦</span>
            <span className="kpi-label">{t('analytics.totalOrders', 'Total Orders')}</span>
          </div>
          <div className="kpi-value">{overview.totalOrders}</div>
          <div className="kpi-details">
            <span className="detail-item success">✓ {overview.completedOrders} completed</span>
            <span className="detail-item danger">✗ {overview.cancelledOrders} cancelled</span>
          </div>
        </div>

        <div className="kpi-card avg-order">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">🧾</span>
            <span className="kpi-label">{t('analytics.avgOrderValue', 'Avg Order Value')}</span>
          </div>
          <div className="kpi-value">
            <PriceTag amount={overview.avgOrderValue} currency={currency} size="lg" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </div>
          <div className="kpi-subtext">{overview.uniqueTables} {t('analytics.uniqueTablesServed')}</div>
        </div>

        <div className="kpi-card prep-time">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">⏱️</span>
            <span className="kpi-label">{t('analytics.avgPrepTime', 'Avg Prep Time')}</span>
          </div>
          <div className="kpi-value">{overview.avgPrepTime} min</div>
          <div className="kpi-subtext">
            Range: {overview.minPrepTime}–{overview.maxPrepTime} min
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card full-width">
          <div className="chart-header">
            <h3>{t('analytics.salesTrend', 'Sales Trend')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesTrendFormatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(v) => formatCurrency(v)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [name === t('analytics.chartOrders') ? value : formatCurrency(value), name]}
                  labelFormatter={(label) => `${t('analytics.dateLabel')}: ${label}`}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0F5B45"
                  strokeWidth={3}
                  name={t('analytics.chartRevenue')}
                  dot={{ fill: '#0F5B45', r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="order_count"
                  stroke="#10b981"
                  strokeWidth={2}
                  name={t('analytics.chartOrders')}
                  dot={{ fill: '#10b981', r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>{t('analytics.peakHours', 'Peak Hours')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={peakHours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="hour"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(hour) => `${hour}:00`}
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(hour) => `${hour}:00 – ${Number(hour) + 1}:00`}
                />
                <Bar
                  dataKey="orderCount"
                  fill="#0F5B45"
                  radius={[8, 8, 0, 0]}
                  name="Orders"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>{t('analytics.categoryRevenue', 'Revenue by Category')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categories}
                  dataKey="total_revenue"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => entry.category}
                >
                  {categories.map((entry, index) => (
                    <Cell key={`cell-${entry.category}-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <h3>{t('analytics.topItems', 'Top Selling Items')}</h3>
        </div>
        <div className="table-content">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('analytics.item', 'Item')}</th>
                <th>{t('analytics.category', 'Category')}</th>
                <th className="text-right">{t('analytics.ordered', 'Times Ordered')}</th>
                <th className="text-right">{t('analytics.quantity', 'Quantity')}</th>
                <th className="text-right">{t('analytics.revenue', 'Revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {topItems.map((item, index) => (
                <tr key={item.id}>
                  <td className="rank-cell">
                    {index === 0 && <span className="medal gold" aria-label="1st">🥇</span>}
                    {index === 1 && <span className="medal silver" aria-label="2nd">🥈</span>}
                    {index === 2 && <span className="medal bronze" aria-label="3rd">🥉</span>}
                    {index > 2 && <span className="rank">{index + 1}</span>}
                  </td>
                  <td className="item-cell">
                    <div className="item-info">
                      {getImageUrl(item.image) && (
                        <img src={getImageUrl(item.image)} alt="" className="item-thumb" />
                      )}
                      <span className="item-name">{item.name}</span>
                    </div>
                  </td>
                  <td>{item.category || '–'}</td>
                  <td className="text-right">{item.times_ordered}</td>
                  <td className="text-right">{item.total_quantity}</td>
                  <td className="text-right revenue-cell">
                    <PriceTag amount={item.total_revenue} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {topItems.length === 0 && (
            <p className="analytics-empty-table">{t('analytics.topItems')} – {t('analytics.noDataForPeriod')}.</p>
          )}
        </div>
      </div>

      <div className="status-summary-card">
        <div className="card-header">
          <h3>{t('analytics.orderStatus', 'Order Status Summary')}</h3>
        </div>
        <div className="status-grid">
          {orderStatus.statusBreakdown?.map((s) => (
            <div key={s.status} className={`status-item status-${s.status}`}>
              <div className="status-count">{s.count}</div>
              <div className="status-label">{s.status}</div>
              <div className="status-value">
                <PriceTag amount={s.total_value} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
              </div>
            </div>
          ))}
        </div>

        {orderStatus.cancellationReasons?.length > 0 && (
          <div className="cancellation-reasons">
            <h4>{t('analytics.cancellationReasons', 'Top Cancellation Reasons')}</h4>
            <div className="reasons-list">
              {orderStatus.cancellationReasons.map((reason, index) => (
                <div key={index} className="reason-item">
                  <span className="reason-text">{reason.cancellation_reason || t('analytics.noReasonProvided')}</span>
                  <span className="reason-count">{reason.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </AppShell>
  );
};

export default AnalyticsDashboard;
